import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../auth/authStore';

interface Props {
  /**
   * Required role to access this subtree.
   * Omit (or pass undefined) to allow any authenticated user regardless of role.
   */
  role?: 'user' | 'admin';
}

/**
 * Wraps a set of routes requiring authentication and optionally a specific role.
 *
 * - Not authenticated        → redirect to /
 * - role specified, wrong    → redirect to the correct home for their role
 * - role unspecified or match → render <Outlet />
 */
export function ProtectedRoute({ role }: Props) {
  const { user, loading, hydrate, step } = useAuthStore();
  const location = useLocation();

  // Hydrate on mount (e.g. direct URL navigation)
  useEffect(() => {
    if (step === 'email') hydrate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: '#0d0d0d', color: '#888',
        fontFamily: 'sans-serif', fontSize: 14,
      }}>
        Verificando sessão…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (role !== undefined && user.role !== role) {
    // Send to the correct home for their actual role
    return <Navigate to={user.role === 'admin' ? '/admin' : '/user'} replace />;
  }

  return <Outlet />;
}
